import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, addDoc, doc, updateDoc, increment,getDoc } from 'firebase/firestore';
import BackHomeButton from '../components/BackHomeButton';

import { functions } from '../firebase'; // Ensure path is correct
import { httpsCallable } from 'firebase/functions';

const SimilarQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { Quiz_Text, subject, topic, format, standard, lesson,lessonId = 'Unknown', school, originalQuizId } = location.state || {};

  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState(0);
  const [newQuizText, setNewQuizText] = useState("");
  
  const [timeLeft, setTimeLeft] = useState(30);
  const [isGenerating, setIsGenerating] = useState(true);
  const [qloading, setqLoading] = useState(false);
  const countdownTimeoutRef = useRef(null);

  // Generate Similar Quiz on Load
  useEffect(() => {
    if (!Quiz_Text || Quiz_Text === 'No_Content') {
      alert("Missing data to generate a similar quiz.");
      navigate('/Old_Home');
      return;
    }

    const generateSimilar = async () => {

      const callGemini = httpsCallable(functions, 'callGemini');
      
      try {

                let lessonText = 'Unknown';

        if (lessonId && lessonId !== 'Unknown') {
          try {
            const lessonSnap = await getDoc(doc(db, 'Saved_lessons', lessonId));
            lessonText = lessonSnap.data()?.text?.trim() || 'Unknown';
          } catch {
            lessonText = 'Unknown';
          }
        }

        const prompt = `You are a strict teacher. I need completely new 20 questions for standard ${standard} on Subject: ${subject}, Topic: ${topic}. 
        Format: ${format}.

               
        CRITICAL RULE: Do NOT repeat any questions from this previous quiz. Make them similar in difficulty but entirely different content:
        ${Quiz_Text}

        Also only frame questions from the given lesson text ${lessonText} ,but If the lesson text is "Unknown", 
        ignore the lesson text.
        
        Respond strictly in this format for each question:
        Question: <question>
        Options: <Option1>| <Option2>| <Option3>| <Option4>
        Answer: <correct answer>
        Separate each question block with one new line. No extra text. Do not number the questions.`;

          
    const result = await callGemini({
      contents: [{ role: "user", parts: [{ text: prompt }] }],
    });

    // 🛡️ Secure Note: The App Check token was sent automatically!
    // result.data contains the body of the response from your backend
    const content =  result.data.candidates?.[0]?.content?.parts?.[0]?.text;



       
        if (!content) throw new Error("AI returned empty content");

        setNewQuizText(content);

        const parseAIResponse = (responseText) => {
          const lines = responseText.trim().split('\n');
          const questionLine = lines.find(line => line.toLowerCase().startsWith('question:'));
          const answerLine = lines.find(line => line.toLowerCase().startsWith('answer:'));
          const optionsLine = format === 'MCQ' || format === 'PatternMaths' ? lines.find(line => line.toLowerCase().startsWith('options:')) : null;

          let options = [];
          if (optionsLine) {
            options = optionsLine.replace(/options:/i, '').split('|').map(opt => opt.trim());
          }

          return {
            question: questionLine?.replace(/question:/i, '').trim(),
            type: format === 'MCQ' ? 'mcq' : 'text',
            options,
            answer: answerLine?.replace(/answer:/i, '').trim(),
          };
        };

        const questionsArray = content.split('\n\n').map(qBlock => parseAIResponse(qBlock)).filter(Boolean);
        setAllQuestions(questionsArray);
        if (questionsArray.length > 0) setCurrentQuestion(questionsArray[0]);
        setIsGenerating(false);

      } catch (err) {
        console.error("Generation failed:", err);
        alert("Failed to generate similar quiz. The AI might be busy.");
        navigate('/Old_Home');
      }
    };

    generateSimilar();
  }, [Quiz_Text, format, standard, subject, topic,lessonId, navigate]);

  // Timer Logic
  useEffect(() => {
    if (isFinished || !currentQuestion || isGenerating) return;
    setTimeLeft(30); 
    const tick = () => {
      setTimeLeft(prev => {
        if (prev <= 1) { handleSubmitAnswer(true); return 0; }
        return prev - 1;
      });
    };
    countdownTimeoutRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownTimeoutRef.current);
  }, [questionIndex, currentQuestion, isFinished, isGenerating]);

  const handleSubmitAnswer = async (isAuto = false) => {
    if (qloading) return;
    setqLoading(true);

    const userAns = (selectedOption || '').toString();
    const isCorrect = userAns.toLowerCase().trim() === (currentQuestion.answer || '').toLowerCase().trim();

    const result = { question: currentQuestion.question, userAnswer: userAns, correctAnswer: currentQuestion.answer, isCorrect };

    const finalAnswers = [...userAnswers, result];
    setUserAnswers(finalAnswers);
    if (isCorrect) setScore(prev => prev + 1);

    if (questionIndex + 1 >= allQuestions.length) {
      setIsFinished(true);
      await saveResults(finalAnswers);
    } else {
      setSelectedOption('');
      setQuestionIndex(prev => prev + 1);
      setCurrentQuestion(allQuestions[questionIndex + 1]);
    }
    setqLoading(false);
  };

const saveResults = async (finalAnswers) => {
    const finalScore = finalAnswers.filter(ans => ans.isCorrect).length;
    try {
      // 1. Log the new result (Bulletproofed against undefined)
      await addDoc(collection(db, 'AI_Results'), {
        uid: auth?.currentUser?.uid || "Unknown",
        subject: subject || "Unknown",
        topic: topic || "Unknown",
        format: format || "Unknown",
        lesson: lesson || "Unknown",
        lessonId: lessonId || 'Unknown',
        standard: standard || "Unknown",
        school: school || "Unknown", // 👈 Now included!
        score: finalScore,
        totalQuestions: finalAnswers.length,
        answers: finalAnswers,
        timestamp: new Date(),
        Quiz_Text: newQuizText || "No_Content", 
        isSimilar: true
      });

      // 2. Increment counter on the original/parent document
      if (originalQuizId) {
        await updateDoc(doc(db, "AI_Results", originalQuizId), {
          similar_generation_count: increment(1)
        });
      }
    } catch (err) {
      console.error("Error saving similar quiz:", err);
    }
  };

  if (isGenerating) {
    return (
      <div style={{ textAlign: 'center', marginTop: '100px', fontFamily: 'sans-serif' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #34d399', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', fontSize: '18px', color: '#666' }}>Generating a brand new similar quiz...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

if (isFinished) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-slate-50 to-white py-4 sm:py-8 px-3 sm:px-6 lg:px-8 font-sans relative">
        <div className="w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-xl rounded-[1rem] sm:rounded-[2rem] shadow-2xl shadow-indigo-500/10 border border-white p-4 sm:p-8">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mb-2">Similar Test Completed 🎉</h2>
            <div className="inline-block bg-indigo-100 text-indigo-800 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-black text-lg sm:text-2xl shadow-sm">
              Your Score: {score} / {allQuestions.length}
            </div>
          </div>

          <h3 className="text-sm sm:text-lg font-bold text-slate-700 mb-4 border-b border-slate-200 pb-2">Answer Breakdown:</h3>
          
          <div className="space-y-3 sm:space-y-4 mb-8">
            {userAnswers.map((ans, index) => (
              <div key={index} className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all ${ans.isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <p className="font-bold text-slate-800 text-[11px] sm:text-sm mb-2"><span className="text-indigo-500 mr-1">Q{index + 1}.</span> {ans.question}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] sm:text-sm">
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">Your Answer:</span> {ans.userAnswer || <span className="italic text-slate-400">Blank</span>}</p>
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">Correct Answer:</span> {ans.correctAnswer}</p>
                </div>
                <div className={`mt-3 inline-flex items-center px-2.5 py-1 rounded-md text-[9px] sm:text-xs font-bold uppercase tracking-wider ${ans.isCorrect ? 'bg-emerald-200 text-emerald-800' : 'bg-rose-200 text-rose-800'}`}>
                  {ans.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                </div>
              </div>
            ))}
          </div>

          {/* Share Button Block */}
          <div className="flex justify-center mb-6">
            <button 
              onClick={() => navigate('/Email_Quiz', { 
                state: { 
                  Quiz_Text: typeof newQuizText !== 'undefined' ? newQuizText : Quiz_Text, 
                  subject, 
                  topic, 
                  format, 
                  standard, 
                  lesson, 
                  school 
                }
              })}
              className="bg-violet-500 hover:bg-violet-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all flex items-center gap-2 active:scale-95"
            >
              ↗️ Share this Quiz
            </button>
          </div>

          <div className="flex justify-center">
            <BackHomeButton />
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '30px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif' }}>
      
      <BackHomeButton />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontWeight: 'bold', color: '#666' }}>
        <span>Question {questionIndex + 1} of {allQuestions.length}</span>
        <span style={{ color: '#d32f2f' }}>⏳ {timeLeft}s</span>
      </div>
      
      <h2 style={{ marginBottom: '20px', color: '#333' }}>{currentQuestion.question}</h2>

      <div style={{ marginBottom: '20px' }}>
        {currentQuestion.options && currentQuestion.options.length > 0 ? (
          currentQuestion.options.map((opt, i) => (
            <button key={i} onClick={() => setSelectedOption(opt)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', marginBottom: '12px',
                background: selectedOption === opt ? '#4caf50' : '#f5f5f5',
                color: selectedOption === opt ? '#fff' : '#333',
                border: selectedOption === opt ? '2px solid #388e3c' : '1px solid #ccc',
                borderRadius: '10px', cursor: 'pointer', fontWeight: '500'
              }}>
              {opt}
            </button>
          ))
        ) : (
           <input type="text" placeholder="Type answer..." value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)} 
            style={{ width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '10px', fontSize: '1rem' }} />
        )}
      </div>

      <button onClick={() => handleSubmitAnswer(false)} disabled={qloading || !selectedOption} 
        style={{
          background: '#4caf50', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '10px',
          fontSize: '1rem', cursor: (qloading || !selectedOption) ? 'not-allowed' : 'pointer', width: '100%', opacity: (!selectedOption) ? 0.5 : 1
        }}>
        {questionIndex + 1 === allQuestions.length ? 'Finish' : 'Next'}
      </button>
    </div>
  );
};

export default SimilarQuiz;